import {
	Dialog,
	Form,
	FormattedText,
	FormItem,
	Toolbar
} from "client/Components";
import {
	AlertManager,
	MediaQueryKind,
	useAlerts,
	useUnmountedCancellation,
	useWorkspaceMediaQuery
} from "client/Hooks";
import {
	getOpenLocalizationsForNewDocumentType,
	OpenLocalizations
} from "client_legacy/Documents/openLocalizations";
import { localize } from "client_legacy/ndaw/culture";
import { documentsConfiguration } from "client_legacy/ndaw/workspaceAbilitiesStatusProvider";
import {
	ReactElement,
	useCallback,
	useMemo,
	useState
} from "react";
import {
	ItemPicker,
	CommonItemPickerOptions,
	ItemPickerOptions,
	SharePointItemPickerOptions,
	WebDavItemPickerOptions,
	ItemPickerLocation,
	WebDavPickerLocation,
	SharePointPickerLocation,
	ItemPickerMode,
	ItemPickerStoreKind
} from "../ItemPicker/ItemPicker";
import { isNil } from "client/Misc/utils";
import { never } from "client_legacy/ndaw/errors";
import { showOpeningInLocalEditorWithOptionToOpenInOnlineEditor } from "client_legacy/Documents/misc";
import { allowToOpenInLocalApp } from "client_legacy/Documents/SharePoint/documentOpenPossibilities";
import { openFileInWopi } from "client_legacy/Documents/SharePoint/documentOpener";
import { open as openWebDavInWopi} from "client_legacy/Documents/WebDav/wopiOpener";
import { open as openSharePointInLocalApp } from "client_legacy/Documents/SharePoint/Actions/openInLocalAppAction";
import { open as openWebDavInLocalApp } from "client_legacy/Documents/WebDav/localAppOpener";
import {
	DocumentStoreType,
	NewDocumentType,
	OfficeDocumentEditor
} from "client/Api/Documents/common";
import { IFormHandler, useFormHandler } from "client/Forms";
import { createDocument as createWebDavDocument } from "client/Api/Documents/WebDav/webDavActions";
import {
	createFile as createSharePointFile,
	DocumentCreationResult
} from "client/Api/Documents/SharePoint/sharePointAction";
import { SharePointEvent, publish as sharePointPublish } from "client_legacy/Documents/SharePoint/documentEvents";
import { WebDavEvent, publish as webDavPublish } from "client_legacy/Documents/WebDav/events";
import { TextInputWithExtension } from "./TextInputWithExtension";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { getItemsInPreferredOrder, UserEditorPreference } from "client_legacy/Documents/officeDocumentsOpenPreference";
import { useEditorPreference } from "../useEditorPreference";
import {
	convertServerErrors,
	createValidationConfiguration,
	InputValidationResult,
	InputValidationResultKind,
	IValidationConfiguration
} from "client/Validation";
import { inline } from "client/Validation/rules";
import {
	getIllegalCharacters,
	isValid as isSharePointNameValid
} from "client_legacy/Documents/SharePoint/documentNamePolicy";
import {
	isValid as isWebDavNameValid
} from "client_legacy/Documents/WebDav/documentNamePolicy";
import { HttpRequestError } from "client/Api/httpClient";
import { DocumentsErrorModel, DocumentsViewType } from "client/Api/Documents/SharePoint/common";
import { ClientValidationErrorModel } from "client/Api/common";
import { FileSystemItemModel, WebDavServerModel } from "client/Api/Documents/WebDav/webDav";

export type NewDocumentDialogProps = {
	onClose: () => void;
	newDocumentType: NewDocumentType;
};

export const NewDocumentDialog = (props: NewDocumentDialogProps): ReactElement => {
	const {
		newDocumentType,
		onClose
	} = props;

	const [location, setLocation] = useState<ItemPickerLocation | undefined>(undefined);
	const [isBusy, setIsBusy] = useState<boolean>(false);

	const globalCancellation: AbortSignal = useUnmountedCancellation();

	const isMobile: boolean = useWorkspaceMediaQuery(MediaQueryKind.Mobile);

	const editorPreference: UserEditorPreference = useEditorPreference();

	const isSharedWithMeViewRoot: boolean = location?.kind === DocumentStoreType.SharePoint
		&& location.folder
			.view
			.type === DocumentsViewType.SharedWithMeView
		&& location.folder
			.isRoot;

	const isButtonDisabled: boolean = isNil(location) || isSharedWithMeViewRoot;

	const alertManager: AlertManager = useAlerts();

	const [formData, setFormData] = useState<CreateDocumentData>({
		name: "",
		location: undefined
	});

	const validationConfiguration = useMemo(
		(): IValidationConfiguration<CreateDocumentData> => {

			let result: IValidationConfiguration<CreateDocumentData> = staticValidationConfiguration;

			result = result.clone();

			if (isNil(location)) {
				return result;
			}

			let illegalCharacters: readonly string[] = [];

			async function getCharacters(): Promise<void> {
				try {
					setIsBusy(true);

					const result: readonly string[] = await getIllegalCharacters();

					illegalCharacters = result;
					setIsBusy(false);
				} catch (e) {
					console.error(e);
					alertManager.showError("_GeneralErrorMessage");
					setIsBusy(false);
				}
			}

			getCharacters();

			result.forScalar("name")
				.add(
					inline((value: string): InputValidationResult => {
						let errorMessage: string | null = null;

						switch (location.kind) {
							case DocumentStoreType.SharePoint:
								if (!isSharePointNameValid(value, illegalCharacters)) {
									errorMessage = localize("_ContainsIllegalCharacters")
										.replace("{0}", illegalCharacters.join(" "));
								}
								break;
							case DocumentStoreType.WebDav:
								if (!isWebDavNameValid(value)) {
									errorMessage = localize("_ContainsIllegalCharactersForWebDav");
								}
								break;
							default:
								never(location);
						}

						if (errorMessage) {
							return {
								kind: InputValidationResultKind.Invalid,
								errorMessage
							};
						}

						return {
							kind: InputValidationResultKind.Valid
						};
					})
						.validateOn("formSubmit")
						.fixErrorOn("inputFinished")
				);

			return result;
		},
		[
			alertManager,
			location
		]
	);

	const formHandler: IFormHandler<CreateDocumentData> = useFormHandler(
		formData,
		setFormData,
		validationConfiguration
	);

	const openLocalizations: OpenLocalizations = getOpenLocalizationsForNewDocumentType(newDocumentType);

	const buttonsVisibility = useMemo(
		(): ButtonsVisibility => {
			if (location?.kind === DocumentStoreType.SharePoint) {
				return {
					isOpenOnlineAvailable: true,
					isOpenLocalAvailable: allowToOpenInLocalApp(),
				};
			}

			const { webDav } = documentsConfiguration();

			if (location?.kind === DocumentStoreType.WebDav && webDav) {
				return {
					isOpenOnlineAvailable: webDav.wopi.isEnabled,
					isOpenLocalAvailable: webDav.localApp.isEnabled,
				};
			}

			return {
				isOpenOnlineAvailable: false,
				isOpenLocalAvailable: false
			};
		},
		[location?.kind]
	);

	const extensionAndTitle = useMemo(
		(): { title: string; extension: string; } => {
			switch (newDocumentType) {
				case NewDocumentType.Word:
					return {
						title: localize("_CreateANewWordDocument"),
						extension: ".docx"
					};
				case NewDocumentType.Excel:
					return {
						title: localize("_CreateANewExcelWorkbook"),
						extension: ".xlsx"
					};
				case NewDocumentType.PowerPoint:
					return {
						title: localize("_CreateANewPowerPointPresentation"),
						extension: ".pptx"
					};
				case NewDocumentType.OneNote:
					return {
						title: localize("_CreateANewOneNoteNotebook"),
						extension: ""
					};
				default:
					never(newDocumentType);
			}
		},
		[newDocumentType]
	);

	const { extension, title } = extensionAndTitle;

	const options: ItemPickerOptions = useMemo(
		() => {
			const supportedStores: readonly ItemPickerStoreKind[] = getSupportedStoreKinds(newDocumentType);

			const sharePointOptions: SharePointItemPickerOptions = supportedStores.includes(DocumentStoreType.SharePoint)
				? {
					isEnabled: true,
					shouldShowPersonalViews: true,
					availableDestination: { kind: "all" }
				}
				: { isEnabled: false };

			const webDavOptions: WebDavItemPickerOptions = supportedStores.includes(DocumentStoreType.WebDav)
					? {
						isEnabled: true,
						availableDestination: { kind: "all" }
					}
					: { isEnabled: false };

			const commonOptions: CommonItemPickerOptions = {
				mode: ItemPickerMode.SingleFolder,
				creationOptions: { folder: true }
			};

			return {
				sharePointOptions,
				webDavOptions,
				commonOptions
			};
		},
		[newDocumentType]
	);

	const createAndPublishWebDavDocument = useCallback(
		async (location: WebDavPickerLocation): Promise<FileSystemItemModel | undefined> => {
			try {
				const newWebDavFile: FileSystemItemModel = await createWebDavDocument(
					location.folder.server.id,
					{
						folderId: location.folder
							.id,
						documentType: newDocumentType,
						name: formHandler.value
							.name + extension,
					},
					globalCancellation
				);

				webDavPublish(
					WebDavEvent.CreatedOrReplaced,
					{
						serverId: location.folder.server.id,
						item: newWebDavFile
					}
				);

				return newWebDavFile;
			} catch (e) {
				let wasHandled: boolean = false;

				if (e instanceof HttpRequestError && e.statusCode === 400) {
					try {
						const errors = e.readJson<ClientValidationErrorModel[]>();

						formHandler.validation.setServerErrors(
							convertServerErrors(errors)
						);
						wasHandled = true;
					} catch (exception) {
						console.error(exception);
					}
				}

				if (!wasHandled) {
					console.error(e);
					alertManager.showError(localize("_GeneralErrorMessage"));
				}

				return undefined;
			}
		},
		[
			alertManager,
			extension,
			formHandler.validation,
			formHandler.value.name,
			newDocumentType,
			globalCancellation
		]
	);

	const createAndPublishSharePointDocument = useCallback(
		async (location: SharePointPickerLocation): Promise<DocumentCreationResult | undefined> => {
			try {
				const file: DocumentCreationResult = await createSharePointFile(
					{
						documentType: newDocumentType,
						folderId: location.folder.id,
						location: location.folder.location,
						name: (formHandler.value.name + extension).trimEnd(),
					},
					globalCancellation
				);

				sharePointPublish(SharePointEvent.AddedOrReplaced, file.newItem);

				if (file.suffix > 0) {
					alertManager.showSuccess(
						<FormattedText
							text={localize("_ReplacedNameForExistedFileName")}
							thingsToReplace={[
								{ what: "{0}", filler: `(${file.suffix})` },
							]}
						/>
					);
				}

				return file;
			} catch (e) {
				let wasHandled: boolean = false;

				if (e instanceof HttpRequestError && e.statusCode === 400) {
					const error = e.readJson<DocumentsErrorModel>();

					if ("message" in error) {
						try {
							const message: ClientValidationErrorModel = {
								propertyName: error.field,
								errorMessage: error.message
							};

							formHandler.validation.setServerErrors(
								convertServerErrors([message])
							);
							wasHandled = true;
						} catch (exception) {
							console.error(exception);
						}
					}
				}

				if (!wasHandled) {
					console.error(e);
					alertManager.showError(localize("_GeneralErrorMessage"));
				}

				return undefined;
			}
		},
		[
			alertManager,
			extension,
			formHandler.validation,
			formHandler.value.name,
			newDocumentType,
			globalCancellation
		]

	);

	const openOnLocalAppHandler = useCallback(
		async (): Promise<void> => {
			try {
				if (isNil(location)) {
					throw new Error("no location selected");
				}

				setIsBusy(true);

				const validationResult: InputValidationResult = await formHandler
					.validation
					.validate();

				if (validationResult.kind === InputValidationResultKind.Invalid) {
					setIsBusy(false);
					return;
				}

				switch (location.kind) {
					case DocumentStoreType.SharePoint:
						const file: DocumentCreationResult | undefined = await createAndPublishSharePointDocument(location);

						if (isNil(file)) {
							setIsBusy(false);
							return;
						}

						try {
							openSharePointInLocalApp(file.newItem);

							showOpeningInLocalEditorWithOptionToOpenInOnlineEditor(
								openLocalizations,
								() => openFileInWopi(file.newItem)
							);
						} catch {
							alertManager.showError(localize("_UnableToOpenDocumentType"));
							setIsBusy(false);
							return;
						}

						break;
					case DocumentStoreType.WebDav:
						const server: WebDavServerModel = location.folder
							.server;

						const newWebDavFile: FileSystemItemModel | undefined = await createAndPublishWebDavDocument(location);

						if (isNil(newWebDavFile)) {
							setIsBusy(false);
							return;
						}

						openWebDavInLocalApp(server, newWebDavFile);
						const { webDav } = documentsConfiguration();

						if (!webDav) {
							throw new Error("WebDav is not available");
						}

						if (webDav
								.wopi
								.isEnabled
						) {
							const { id, name } = newWebDavFile;
							showOpeningInLocalEditorWithOptionToOpenInOnlineEditor(
								openLocalizations,
								() => openWebDavInWopi(server.id, id, name)
							);
						}
						break;
					default:
						never(location);
				}

				setIsBusy(false);
				onClose();
			} catch (e){
				if (isCancelledError(e)) {
					return;
				}

				setIsBusy(false);

				if (formHandler.validation.tryProcessServerError(e)) {
					return;
				}
				alertManager.showError(localize("_GeneralErrorMessage"));
			}
		},
		[
			location,
			onClose,
			createAndPublishSharePointDocument,
			createAndPublishWebDavDocument,
			openLocalizations,
			alertManager,
			formHandler.validation
		]
	);

	const openOnlineAppHandler = useCallback(
		async (): Promise<void> => {
			try {
				if (isNil(location)) {
					throw new Error("no location selected");
				}

				setIsBusy(true);

				const validationResult: InputValidationResult = await formHandler
					.validation
					.validate();

				if (validationResult.kind === InputValidationResultKind.Invalid) {
					setIsBusy(false);
					return;
				}

				switch (location.kind) {
					case DocumentStoreType.SharePoint:
						const sharePointFile: DocumentCreationResult | undefined = await createAndPublishSharePointDocument(location);

						if (isNil(sharePointFile)) {
							setIsBusy(false);
							return;
						}

						openFileInWopi(sharePointFile.newItem);
						break;
					case DocumentStoreType.WebDav:
						const webDavFile: FileSystemItemModel | undefined = await createAndPublishWebDavDocument(location);

						if (isNil(webDavFile)) {
							setIsBusy(false);
							return;
						}

						const { id, name } = webDavFile;
						const serverId = location.folder
							.server
							.id;

						openWebDavInWopi(
							serverId,
							id,
							name
						);
						break;
					default:
						never(location);
				}

				setIsBusy(false);
				onClose();
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				setIsBusy(false);

				if (formHandler.validation.tryProcessServerError(e)) {
					return;
				}

				alertManager.showError(localize("_GeneralErrorMessage"));
			}
		},
		[
			alertManager,
			createAndPublishSharePointDocument,
			createAndPublishWebDavDocument,
			formHandler.validation,
			location,
			onClose
		]
	);

	const renderOpenOnlineButton = (): ReactElement | null => {
		if (buttonsVisibility.isOpenOnlineAvailable) {
			return (
				<Toolbar.Button
					key="open-online"
					kind="primary"
					onClick={openOnlineAppHandler}
					text={openLocalizations.online.action}
					isDisabled={isButtonDisabled}
					trackingId="open-online"
				/>
			);
		}

		return null;
	};

	const renderOpenInLocalButton = (): ReactElement | null => {
		if (buttonsVisibility.isOpenLocalAvailable) {
			return (
				<Toolbar.Button
					key="open-in-local-app"
					kind="primary"
					onClick={openOnLocalAppHandler}
					text={openLocalizations.local.action}
					isDisabled={isButtonDisabled}
					trackingId="open-in-local-app"
				/>
			);
		}

		return null;
	};

	return (
		<Dialog
			variant="left-slider"
			trackingId="new-document-dialog"
			hasBusyIndicator={isBusy}
			onClose={onClose}
		>
			<Dialog.Header
				title={title}
				onClose={onClose}
				hasBorder
			/>
			<Dialog.Body goesEdgeToEdge>
				<ItemPicker
					options={options}
					location={location}
					onLocationChange={setLocation}
				/>
			</Dialog.Body>
			<Dialog.Footer hasBorder>
				<Form onSubmit="NONE" hasWidthConstrained>
					<FormItem
						label={localize("_DocumentName")}
						isRequired
					>
						<TextInputWithExtension
							attachable={formHandler.for("name")}
							isMobile={isMobile}
							fileExtension={extension}
						/>
					</FormItem>
				</Form>
				<Toolbar>
					{getItemsInPreferredOrder<ReactElement | null>(
						location?.kind === DocumentStoreType.SharePoint
							? documentsConfiguration().sharePoint.defaultOfficeDocumentEditor
							: documentsConfiguration().webDav?.defaultOfficeDocumentEditor ?? OfficeDocumentEditor.Online,
						[
							[
								OfficeDocumentEditor.Online,
								renderOpenOnlineButton
							],
							[
								OfficeDocumentEditor.Local,
								renderOpenInLocalButton
							]
						],
						editorPreference
					)}
					<Toolbar.Button
						kind="secondary"
						onClick={onClose}
						text={localize("_Cancel")}
						trackingId="cancel"
					/>
				</Toolbar>
			</Dialog.Footer>
		</Dialog>
	);
};

NewDocumentDialog.displayName="NewDocumentDialog";

const staticValidationConfiguration = createValidationConfiguration<CreateDocumentData>();

type ButtonsVisibility = {
	isOpenOnlineAvailable: boolean;
	isOpenLocalAvailable: boolean;
};

type CreateDocumentData = {
	name: string;
	location: ItemPickerLocation | undefined;
};

type NewDocumentDialogInitialData = Omit<NewDocumentDialogProps, "onClose">;

type DialogState = {
	isOpen: false;
} | {
	isOpen: true;
	handlers: PromiseHandlers<void>;
	newDocumentType: NewDocumentType;
};

export function useNewDocumentDialog(): [
	dialogElement: ReactElement | null,
	open: (props: NewDocumentDialogInitialData) => Promise<void>
] {
	const [dialogState, setDialogState] = useState<DialogState>({isOpen: false});

	const renderDialog = useCallback(
		(): ReactElement | null => {
			return dialogState.isOpen
				? (
					<NewDocumentDialog
						newDocumentType={dialogState.newDocumentType}
						onClose={
							(): void => {
								dialogState.handlers
									.resolve();
								setDialogState({ isOpen: false });
							}
						}
					/>
				)
				: null;
		},
		[dialogState]
	);

	const openDialogHandler = useCallback(
		(props: NewDocumentDialogInitialData): Promise<void> => {
			return new Promise(
				(resolve, reject) => {
					setDialogState({
						isOpen: true,
						handlers: {
							resolve,
							reject
						},
						newDocumentType: props.newDocumentType
					});
				}
			);
		},
		[]
	);

	return [
		renderDialog(),
		openDialogHandler
	];
}

export function isNewDocumentDialogSupported(newDocumentType: NewDocumentType): boolean {
	return getSupportedStoreKinds(newDocumentType).length > 0;
}

function getSupportedStoreKinds(newDocumentType: NewDocumentType): readonly ItemPickerStoreKind[] {
	const { sharePoint, webDav } = documentsConfiguration();

	const result: ItemPickerStoreKind[] = [];

	if (sharePoint.isConfigured) {
		result.push(DocumentStoreType.SharePoint);
	}

	if (newDocumentType !== NewDocumentType.OneNote
		&& (webDav && webDav.isConfigured)
	) {
		result.push(DocumentStoreType.WebDav);
	}

	return result;
}